import {
  ExistingItemHandling,
  type Product,
  type Variant,
} from '@scayle/storefront-core'
import useUiState from './ui/useUiState'
import type { RxConfiguratorProps } from '~/components/rx/RxConfigurator/RxConfigurator'
import type { IAddOrUpdateItems } from '~/rpcMethods/basket'

export function useRxConfigurator(
  product: MaybeRefOrGetter<Product | undefined>,
  variant: MaybeRefOrGetter<Variant | undefined>,
  initialStep: number = 0,
) {
  const { $featureToggle } = useNuxtApp()
  const { addOrUpdateItems } = useFimBasket()
  const { setErrorState } = useRxConfiguratorState()
  const { trackCTAClick, aggregateRxConfigurationPrice, trackAddToBasket } =
    useTrackingEvents()

  const router = useRouter()

  let cleanup: ReturnType<typeof useEventListener>

  // Enables opening RxConfigurator when navigating back in browser history
  const skipBrowserNav = ref(false)
  const onBrowserNav = () => {
    if (skipBrowserNav.value) {
      skipBrowserNav.value = false
      return
    }
    if (isNaN(history.state?.rxConfiguratorStep)) {
      closeRxConfiguratorOverlay()
      removeRxQueryParam()
    } else {
      openRxConfiguratorOverlay()
    }
  }

  onScopeDispose(() => {
    if (!getQueryParam(router.currentRoute.value.query, 'rxStep')) {
      closeRxConfiguratorOverlay()
      removeRxQueryParam()
    }

    if (cleanup) {
      cleanup()
    }
  })

  onMounted(() => {
    cleanup = useEventListener('popstate', onBrowserNav)

    if ((history.state?.virtualHistoryStep || 0) > 0) {
      skipBrowserNav.value = true
      clearVirtualHistory()
    }

    if (
      getQueryParam(router.currentRoute.value.query, 'rxStep') &&
      !isRxConfiguratorOverlayOpen.value
    ) {
      openRxConfiguratorOverlay()
    }
  })

  const removeRxQueryParam = () => {
    if (getQueryParam(router.currentRoute.value.query, 'rxStep')) {
      history.replaceState(
        {},
        '',
        removeQueryParam(router.currentRoute, 'rxStep'),
      )
    }
  }

  const isRxConfiguratorAvailable = computed(() =>
    $featureToggle.isRxConfiguratorEnabled(product),
  )

  const rxConfiguratorProps = computed((): RxConfiguratorProps | undefined => {
    const p = toValue(product)
    const v = toValue(variant)

    if (!p || !v || !isRxConfiguratorAvailable.value) {
      return
    }

    return {
      product: p,
      variant: v,
      initialStep,
    }
  })

  const {
    openRxConfiguratorOverlay,
    closeRxConfiguratorOverlay,
    isRxConfiguratorOverlayOpen,
  } = useUiState()

  const closeRxConfigurator = () => {
    closeRxConfiguratorOverlay()
    removeRxQueryParam()
    // revert all history steps that were added within the RxConfigurator
    skipBrowserNav.value = true
    clearVirtualHistory()
  }

  const convertSerializedToBasketItems = (
    itemsSerialized: ConfiguratorSerialized,
  ): IAddOrUpdateItems['items'] => {
    return itemsSerialized.map((item) => {
      const { variantId, quantity, ...params } = item
      const customData = { ...params.customData, rxOrder: true }
      return { variantId, quantity, params: { ...params, customData } }
    })
  }

  const openRxConfigurator = (event?: any) => {
    openRxConfiguratorOverlay()
    if (event) {
      trackCTAClick({
        productDetails: toValue(product),
        variant: toValue(variant),
        element: event.target,
      })
    }
  }

  const addRxItemsToBasket = async (
    itemsSerialized: ConfiguratorSerialized,
    rxConfiguration: ConfiguratorState,
    promotionId?: MaybeRefOrGetter<string>,
    quantity: MaybeRefOrGetter<number> = 1,
    options?: IAddOrUpdateItems['options'],
    source?: string,
  ) => {
    const p = toValue(product)
    const v = toValue(variant)

    if (!p || !v) {
      return
    }

    try {
      await addOrUpdateItems(
        convertSerializedToBasketItems(itemsSerialized),
        {
          existingItemHandling:
            options?.existingItemHandling ?? ExistingItemHandling.KeepExisting,
          considerItemGroupForUniqueness: true,
        },
        toValue(promotionId),
      )

      const overridePrice = aggregateRxConfigurationPrice(rxConfiguration, v)

      trackAddToBasket(p, Number(toValue(quantity)), toValue(variant), {
        overridePrice,
        source,
      })
    } catch (e) {
      setErrorState(true)
      throw e
    }
  }

  return {
    closeRxConfigurator,
    openRxConfigurator,
    addRxItemsToBasket,
    rxConfiguratorProps,
    isRxConfiguratorAvailable,
  }
}
